/**
 * dedupNormalize — Gemeinsame, seiteneffektfreie Helfer zur Ableitung einer
 * normalisierten Firmen-Domain aus einem Kontakt.
 *
 * WICHTIG (Phase 0 / Fundament):
 *   - Dieses Modul ist rein ADDITIV. Es berechnet nur ein neues, optionales
 *     Feld `lead_domain_dedup`. Es verändert NICHTS am bestehenden E-Mail-/
 *     Namens-Dedup, an Doc-IDs oder am Matching-Verhalten.
 *   - Die Logik ist bewusst identisch zur Server-Variante
 *     `functions/helpers/dedupNormalize.js` und zu `companyResolver.js`,
 *     damit Client und Server bit-identisch normalisieren.
 *
 * Free-Mail-Domains werden NICHT als Firmen-Domain gewertet — sonst würden
 * alle Gmail-/GMX-Kontakte fälschlich als Duplikate derselben "Firma" gelten.
 */

// Muss mit functions/helpers/dedupNormalize.js und companyResolver.js übereinstimmen.
export const FREE_EMAIL_PROVIDERS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.de', 'hotmail.com',
  'hotmail.de', 'outlook.com', 'outlook.de', 'live.com', 'live.de',
  'icloud.com', 'me.com', 'mac.com', 'aol.com', 'gmx.de', 'gmx.net',
  'gmx.at', 'gmx.ch', 'web.de', 't-online.de', 'freenet.de',
  'posteo.de', 'mailbox.org', 'protonmail.com', 'proton.me',
  'tutanota.com', 'tuta.io', 'yandex.com', 'mail.com', 'zoho.com',
]);

// Social-Aggregator-, URL-Shortener- und Content-Plattformen: sind KEINE
// Firmen-Domains und dürfen daher keine Domain-Duplikat-Cluster bilden.
// Phase B (2026-08) — der Audit hat gezeigt, dass ohne diesen Filter
// Kontakte mit `lead_website = linktr.ee/xy` fälschlich als "gleiche Firma"
// zusammengefasst werden (453 Docs in einer Test-World). Der Filter wirkt
// ausschließlich auf `lead_domain_dedup`. Die eigentliche `lead_website`
// bleibt unverändert, sodass User weiter darauf klicken können.
export const NON_COMPANY_DOMAINS = new Set([
  // Link-in-Bio / Aggregatoren
  'linktr.ee', 'linkin.bio', 'bio.site', 'wonderl.ink', 'beacons.ai',
  'campsite.bio', 'lnk.bio', 'many.link', 'msha.ke', 'shor.by',
  'stan.store', 'flow.page', 'stnd.bio',
  // URL-Shortener
  'bit.ly', 'tinyurl.com', 't.co', 'goo.gl', 'ow.ly', 'rebrand.ly',
  'buff.ly', 'is.gd', 'v.gd',
  // Content- / Social-Plattformen (Profil-URLs, keine Firmen-Websites)
  'youtube.com', 'youtu.be', 'tiktok.com', 'instagram.com',
  'facebook.com', 'fb.com', 'twitter.com', 'x.com',
  'linkedin.com', 'lnkd.in', 'pinterest.com', 'threads.net',
  'wa.me', 'chat.whatsapp.com', 't.me', 'discord.gg',
  'medium.com', 'substack.com', 'notion.site', 'notion.so',
  'github.com', 'gitlab.com',
  // Marketplaces / Portale (Profil-URLs)
  'etsy.com', 'ebay.com', 'ebay.de', 'amazon.com', 'amazon.de',
  'fiverr.com', 'upwork.com',
]);

/**
 * Extrahiert den reinen Hostname aus einer Website/URL.
 * Entfernt Protokoll, "www.", Pfad/Query/Fragment und Port.
 */
export const extractDomain = (input) => {
  if (!input || typeof input !== 'string') return '';
  let str = input.trim().toLowerCase();
  str = str.replace(/^https?:\/\//, '');
  str = str.replace(/^www\./, '');
  str = str.split('/')[0].split('?')[0].split('#')[0];
  str = str.split(':')[0];
  return str || '';
};

/**
 * Extrahiert die Domain (Teil nach dem letzten @) aus einer einzelnen E-Mail.
 */
export const extractEmailDomain = (email) => {
  if (!email || typeof email !== 'string') return '';
  const trimmed = email.trim().toLowerCase();
  const atIdx = trimmed.lastIndexOf('@');
  if (atIdx < 0) return '';
  return trimmed.substring(atIdx + 1);
};

/**
 * true, wenn die Domain eine "echte" Firmen-Domain ist. Filtert:
 *   - leere Strings, `"0"`, `"null"` (Sanitizer-Rest)
 *   - Free-Mail-Anbieter (gmail.com, gmx.de, …)
 *   - Social-Aggregatoren / URL-Shortener / Content-Plattformen
 *     (linktr.ee, youtube.com, tiktok.com, …)
 * Der Kern: nur echte, unternehmens-spezifische Domains zählen.
 */
export const isBusinessDomain = (domain) => {
  if (!domain || typeof domain !== 'string') return false;
  if (domain === '0' || domain === 'null' || domain === '-') return false;
  if (!domain.includes('.')) return false;
  if (FREE_EMAIL_PROVIDERS.has(domain)) return false;
  if (NON_COMPANY_DOMAINS.has(domain)) return false;
  return true;
};

/**
 * Leitet die Dedup-Domain eines Kontakts ab.
 *
 * Reihenfolge:
 *   1. Domain aus `lead_website` (falls Business-Domain)
 *   2. sonst erste Business-Domain aus `lead_email` (komma-separiert möglich)
 *   3. sonst '' (keine Domain-Identität)
 *
 * Gibt IMMER einen String zurück (nie null/undefined), damit das Feld sicher
 * indizierbar bleibt.
 */
export const computeDomainDedup = (contact) => {
  if (!contact || typeof contact !== 'object') return '';

  const websiteDomain = extractDomain(contact.lead_website);
  if (websiteDomain && isBusinessDomain(websiteDomain)) {
    return websiteDomain;
  }

  const rawEmail = contact.lead_email;
  if (rawEmail && typeof rawEmail === 'string') {
    const emails = rawEmail.split(',').map((e) => e.trim()).filter(Boolean);
    for (const email of emails) {
      const emailDomain = extractEmailDomain(email);
      if (emailDomain && isBusinessDomain(emailDomain)) {
        return emailDomain;
      }
    }
  }

  return '';
};
